import {Component, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges} from '@angular/core';
import {WebviewService} from '@utils/services/webview/webview.service';
import {PaywallType} from '@lib/models/paywall-type/paywall-type';
import {UrlService} from '@utils/services/url/url.service';
import {SubscriptionsHttpService} from '@api/http/subscriptions/subscriptions-http.service';
import { isPlatformServer } from '@angular/common';
import { IUser } from '@api/models/user/user';

export enum AboBannerType {
  Subscription,
  Camera
}

@Component({
  selector: 'lib-abo-banner',
  templateUrl: './abo-banner.component.html',
  styleUrls: ['./abo-banner.component.scss']
})

export class AboBannerComponent implements OnChanges, OnDestroy {

  @Input() public name: string;
  @Input() public type: AboBannerType = AboBannerType.Subscription;
  @Input() private user: IUser;
  protected readonly AboBannerType = AboBannerType;
  public title: string;
  public subtitle: string;
  private fallbackTitle = 'Abo holen'
  private fallbackSubtitle = 'Verein unterstützen'
  private defaultSubtitle = 'Streamen'
  private resizeObserver: ResizeObserver | null = null;
  public show = false;

  constructor(
    private subscriptionsHttp: SubscriptionsHttpService,
    @Inject(PLATFORM_ID) private platformId
  ) { }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.user || changes.user.currentValue) {
      this.show = await this.shouldShowBanner();
      if (!this.show) { return; }
      this.setTitles();
      this.setupResizeObserver();
    }
  }

  ngOnDestroy(): void {
    this.cleanupResizeObserver();
  }

  private async shouldShowBanner(): Promise<boolean> {
    if (this.type === AboBannerType.Camera) {
      return true;
    }

    if (!this.user) {
      return true; // Guest user, show banner
    }

    // Show subscription banner only when user has not subscribed yet
    try {
      const response = await this.subscriptionsHttp.getSubscriptionStatus(this.user._id);
      return !response?.subscribed;
    } catch (error) {
      // Email not set or verified, show banner
      return true;
    }
  }

  private setTitles() {
    switch (this.type) {
      case AboBannerType.Subscription:
        if (this.name) {
          this.title = this.name;
          this.subtitle = this.defaultSubtitle;
        } else {
          this.title = this.fallbackTitle;
          this.subtitle = this.fallbackSubtitle;
        }
        break;
      case AboBannerType.Camera:
        this.title = 'Dein Verein. Dein Stream';
        this.subtitle = 'Jetzt Kamera holen';
    }
  }

  // Prevent text from overflowing by setting fallback titles in observer
  private setupResizeObserver() {
    if (isPlatformServer(this.platformId)) { return; }
    if (this.type !== AboBannerType.Subscription) { return; }
    const textElement = document.getElementById('title');
    if (!textElement) { return; }
    this.resizeObserver = new ResizeObserver(() => {
      if (textElement.scrollWidth > textElement.clientWidth) {
        this.title = this.fallbackTitle;
        this.subtitle = this.fallbackSubtitle;
      }
    });
    this.resizeObserver.observe(textElement);
  }

  public onBannerClick() {
    switch (this.type) {
      case AboBannerType.Subscription:
        if (WebviewService.isWebview) {
          WebviewService.sendPaywallEvent(PaywallType.Abo)
          return
        }
        window.location.href = UrlService.Web + '/abo/subscribe';
        break;
      case AboBannerType.Camera:
        if (WebviewService.isWebview) {
          WebviewService.sendNavigationEvent(UrlService.Web + '/streaming');
          return
        }
        window.location.href = UrlService.Web + '/streaming';
    }

  }

  private cleanupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}
